/**
 * Cart drawer "Complete your purchase" recommendations (snippets/wd-cart-drawer-recommendations.liquid).
 *
 * - Prev/next buttons for the scroll-snap row (RTL aware).
 * - Quick add: posts to cart/add.js with the payload shape wd-eilat-cart.js expects and keeps the drawer open.
 *   Regular drawer (data-drawer="default"): re-renders through the same sections as <cart-drawer-items>.
 *   CTO drawer (data-drawer="cto"): re-renders through <cto-cart-drawer>.updateItems() (?view=cto-cart-drawer-items).
 *   "Added to cart" panel (data-drawer="panel"): re-renders through <wd-cart-added-panel>.refresh() (panel, closed drawer
 *   and cart count) - the cart drawer doesn't open and no new panel opens.
 *
 * The element lives inside the re-rendered drawer markup, so every re-render creates a fresh instance.
 */
if (!customElements.get('wd-cart-drawer-recommendations')) {
  // The CTO drawer markup comes from its own view; only the header cart bubble is refreshed through sections
  const CTO_SECTIONS_TO_RENDER = [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];

  class WdCartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.wd-cart-drawer-recs__track');
      this.arrows = this.querySelectorAll('.wd-cart-drawer-recs__arrow');
      if (!this.track) return;

      this.onScroll = this.updateArrows.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.arrows.forEach((arrow) => arrow.addEventListener('click', () => this.scrollByCard(Number(arrow.dataset.direction))));

      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);

      this.querySelectorAll('.wd-cart-drawer-recs__add').forEach((button) => {
        button.addEventListener('click', () => this.quickAdd(button));
      });
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    get isRtl() {
      return getComputedStyle(this.track).direction === 'rtl';
    }

    /** direction: 1 = towards inline-end (next), -1 = towards inline-start (prev) */
    scrollByCard(direction) {
      const item = this.track.querySelector('.wd-cart-drawer-recs__item');
      const gap = parseFloat(getComputedStyle(this.track.firstElementChild).columnGap) || 0;
      const step = item ? item.offsetWidth + gap : this.track.clientWidth;
      this.track.scrollBy({ left: step * direction * (this.isRtl ? -1 : 1) });
    }

    updateArrows() {
      const { scrollWidth, clientWidth } = this.track;
      // scrollLeft is 0 at the start and negative towards the end in RTL
      const offset = Math.abs(this.track.scrollLeft);
      const maxOffset = scrollWidth - clientWidth;

      this.classList.toggle('is-scrollable', maxOffset > 1);
      this.arrows.forEach((arrow) => {
        arrow.disabled = Number(arrow.dataset.direction) < 0 ? offset <= 1 : offset >= maxOffset - 1;
      });
    }

    get isCto() {
      return this.dataset.drawer === 'cto';
    }

    get isPanel() {
      return this.dataset.drawer === 'panel';
    }

    /** id of the drawer root (#CartDrawer / #CTOCartDrawer / #CartAddedPanel), also the prefix of its heading and errors ids */
    get drawerId() {
      if (this.isPanel) return 'CartAddedPanel';
      return this.isCto ? 'CTOCartDrawer' : 'CartDrawer';
    }

    async quickAdd(button) {
      const data = button.dataset;
      const { isCto, isPanel, drawerId } = this;
      const drawerItems = this.closest('cart-drawer-items');
      const ctoDrawer = this.closest('cto-cart-drawer');
      const panel = this.closest('wd-cart-added-panel');
      if (isPanel ? !panel : isCto ? !ctoDrawer : !drawerItems) return;
      if (button.getAttribute('aria-busy') === 'true') return;

      // Same max quantity guard as the card quick add (product-form.js)
      if (data.productMaxQuantity && typeof checkMaxquantity === 'function') {
        const inCart = parseInt(checkMaxquantity(data.productId), 10) || 0;
        if (inCart + 1 > parseInt(data.productMaxQuantity, 10)) {
          if (typeof quantity_limit_error_message !== 'undefined') alert(quantity_limit_error_message);
          return;
        }
      }

      this.setBusy(button, true);

      // The panel re-renders the cart count itself (panel.refresh())
      const sections = isPanel ? [] : isCto ? CTO_SECTIONS_TO_RENDER : drawerItems.getSectionsToRender();
      const item = {
        id: Number(data.variantId),
        quantity: 1,
        product_type: data.productType,
        properties: {
          _uuid: data.productHandle,
          ...(data.productTradeIn === 'true' && { _tradeInEligible: 'true' }),
        },
      };

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [item],
            ...(sections.length && {
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            }),
          }),
        });
        const state = await response.json();

        if (!response.ok || state.status) {
          this.showError(state.description || state.message);
          this.setBusy(button, false);
          return;
        }

        this.trackAddToCart(button, state);

        const liveRegionText = `${data.productTitle} ${this.dataset.addedMessage}`;
        sections.forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          if (!elementToReplace || !state.sections?.[section.section]) return;
          elementToReplace.innerHTML = new DOMParser()
            .parseFromString(state.sections[section.section], 'text/html')
            .querySelector(section.selector).innerHTML;
        });

        if (isCto) await ctoDrawer.updateItems(state.items);
        if (isPanel) await panel.refresh();

        this.afterRender(drawerId, liveRegionText);
      } catch (error) {
        // Thrown by wd-eilat-cart.js when the customer cancels a cart mode prompt, or a network error
        console.error(error);
        if (this.isConnected) this.setBusy(button, false);
      }
    }

    /** Runs on the old (now detached) instance after the drawer markup was replaced. */
    afterRender(drawerId, liveRegionText) {
      const drawer = document.getElementById(drawerId);
      if (drawerId === 'CartAddedPanel') {
        const liveRegion = document.getElementById('CartAddedPanel-LiveRegion');
        if (liveRegion) liveRegion.textContent = liveRegionText;
      } else if (drawerId === 'CartDrawer') {
        const liveRegion = document.getElementById('CartDrawer-LiveRegionText');
        if (liveRegion) liveRegion.textContent = liveRegionText;

        // Rebind modals, store locator, etc. without fetching the sections again
        if (typeof window.onCartUpdate === 'function') window.onCartUpdate({ skipSectionFetch: true });
      }

      // Keep focus inside the open drawer: next recommendation heading, or the drawer panel itself
      const focusTarget =
        document.getElementById(`${drawerId}-RecommendationsHeading`) ||
        drawer?.querySelector('.drawer__inner') ||
        document.getElementById(`${drawerId}-Heading`);
      if (drawer && focusTarget && typeof trapFocus === 'function') trapFocus(drawer, focusTarget);
    }

    trackAddToCart(button, state) {
      const data = button.dataset;
      const addedLine = state.items?.[0];

      // GA helpers are exposed from product-form.js (window.wdCartCookie / window.wdTriggerGAAddToCart)
      if (addedLine && typeof window.wdCartCookie === 'function') {
        window.wdCartCookie(data.productMpn, addedLine.key, data.productList);
      }
      if (typeof window.wdTriggerGAAddToCart === 'function') {
        window.wdTriggerGAAddToCart(
          data.productComparePrice,
          data.productMpn,
          data.productTitle,
          data.productType,
          data.productList,
          data.productCurrency,
          data.productPrice,
          1,
          data.productMpn,
          data.variantId
        );
      }
      if (typeof window.adobeTrackAddToCart === 'function') {
        window.adobeTrackAddToCart({
          items: [{ id: data.variantId, quantity: 1 }],
          context: 'Cart Drawer Recommendations',
          $trigger: button,
        });
      }
    }

    setBusy(button, busy) {
      button.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('.wd-cart-drawer-recs__add').forEach((addButton) => {
        addButton.disabled = busy;
      });
    }

    showError(message) {
      const errors = document.getElementById(`${this.drawerId}-CartErrors`);
      if (errors) errors.textContent = message || this.dataset.errorMessage;
    }
  }

  customElements.define('wd-cart-drawer-recommendations', WdCartDrawerRecommendations);
}
